import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatListingPublishedAt } from '@redevicer/shared'
import { listingsAPI } from '../api/client'
import { publicAssetUrl } from '../utils/assetUrl'
import { parseListingCommentError } from '../utils/listingCommentError'

const MAX_LEN = 1000

export interface ListingComment {
  id: number
  listing_id: number
  body: string
  created_at: string
  is_own: boolean
  author: {
    id: number
    display_name: string
    avatar_url?: string | null
  }
}

type Props = {
  listingId: number
  token: string | null
  onRequireLogin?: () => void
}

function authorInitial(name: string): string {
  const trimmed = name.trim()
  if (!trimmed) return '?'
  return trimmed.charAt(0).toUpperCase()
}

export default function ListingCommentsSection({ listingId, token, onRequireLogin }: Props) {
  const { t, i18n } = useTranslation()
  const [comments, setComments] = useState<ListingComment[]>([])
  const [loading, setLoading] = useState(true)
  const [posting, setPosting] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [error, setError] = useState('')
  const [errorKind, setErrorKind] = useState<'load' | 'post' | 'delete' | null>(null)
  const [draft, setDraft] = useState('')

  const loadComments = useCallback(async () => {
    try {
      const res = await listingsAPI.listComments(listingId, token)
      setComments(Array.isArray(res.data) ? (res.data as ListingComment[]) : [])
      setError('')
      setErrorKind(null)
    } catch (err) {
      setErrorKind('load')
      setError(parseListingCommentError(err, t))
    } finally {
      setLoading(false)
    }
  }, [listingId, token, t])

  useEffect(() => {
    setLoading(true)
    void loadComments()
  }, [loadComments])

  async function submitComment(e: React.FormEvent) {
    e.preventDefault()
    const body = draft.trim()
    if (!body || posting) return
    if (!token) {
      onRequireLogin?.()
      return
    }
    if (body.length > MAX_LEN) {
      setErrorKind('post')
      setError(t('listing.comments.tooLong', { max: MAX_LEN }))
      return
    }
    setPosting(true)
    setError('')
    setErrorKind(null)
    try {
      const res = await listingsAPI.postComment(listingId, body, token)
      const created = res.data as ListingComment
      setComments((prev) => [...prev, created])
      setDraft('')
    } catch (err) {
      setErrorKind('post')
      setError(parseListingCommentError(err, t))
    } finally {
      setPosting(false)
    }
  }

  async function deleteComment(commentId: number) {
    if (!token || deletingId != null) return
    setDeletingId(commentId)
    setError('')
    setErrorKind(null)
    try {
      await listingsAPI.deleteComment(listingId, commentId, token)
      setComments((prev) => prev.filter((c) => c.id !== commentId))
    } catch (err) {
      setErrorKind('delete')
      setError(parseListingCommentError(err, t))
    } finally {
      setDeletingId(null)
    }
  }

  const errorTitle =
    errorKind === 'load'
      ? t('listing.comments.errorTitleLoad')
      : errorKind === 'delete'
        ? t('listing.comments.errorTitleDelete')
        : t('listing.comments.errorTitlePost')

  return (
    <section className="listing-detail-comments" aria-labelledby="listing-comments-heading">
      <header className="listing-detail-comments__head">
        <h3 id="listing-comments-heading" className="listing-detail-comments__title">
          {t('listing.comments.title')}
        </h3>
        {!loading && comments.length > 0 ? (
          <span className="listing-detail-comments__count">
            {t('listing.comments.count', { count: comments.length })}
          </span>
        ) : null}
      </header>

      {loading ? (
        <p className="listing-detail-comments__loading">{t('listing.comments.loading')}</p>
      ) : comments.length === 0 && !error ? (
        <div className="listing-detail-comments__empty-card">
          <span className="listing-detail-comments__empty-icon" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <p className="listing-detail-comments__empty-title">{t('listing.comments.emptyTitle')}</p>
          <p className="listing-detail-comments__empty-hint">{t('listing.comments.emptyHint')}</p>
        </div>
      ) : comments.length > 0 ? (
        <ul className="listing-detail-comments__list">
          {comments.map((comment) => {
            const avatarSrc = comment.author.avatar_url
              ? publicAssetUrl(comment.author.avatar_url)
              : null
            return (
              <li key={comment.id} className="listing-detail-comments__item">
                <div className="listing-detail-comments__avatar" aria-hidden="true">
                  {avatarSrc ? (
                    <img src={avatarSrc} alt="" />
                  ) : (
                    <span>{authorInitial(comment.author.display_name)}</span>
                  )}
                </div>
                <div className="listing-detail-comments__bubble">
                  <div className="listing-detail-comments__meta-row">
                    <strong className="listing-detail-comments__author">
                      {comment.is_own ? t('listing.comments.you') : comment.author.display_name}
                    </strong>
                    <div className="listing-detail-comments__meta-actions">
                      <time dateTime={comment.created_at} className="listing-detail-comments__time">
                        {formatListingPublishedAt(comment.created_at, i18n.language)}
                      </time>
                      {comment.is_own && token ? (
                        <button
                          type="button"
                          className="listing-detail-comments__delete"
                          onClick={() => void deleteComment(comment.id)}
                          disabled={deletingId === comment.id}
                          aria-label={t('listing.comments.delete')}
                        >
                          {deletingId === comment.id
                            ? t('listing.comments.deleting')
                            : t('listing.comments.delete')}
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <p className="listing-detail-comments__body">{comment.body}</p>
                </div>
              </li>
            )
          })}
        </ul>
      ) : null}

      {error ? (
        <div className="listing-detail-comments__alert" role="alert">
          <p className="listing-detail-comments__alert-title">{errorTitle}</p>
          <p className="listing-detail-comments__alert-body">{error}</p>
        </div>
      ) : null}

      {token ? (
        <form className="listing-detail-comments__form" onSubmit={(e) => void submitComment(e)}>
          <label className="sr-only" htmlFor="listing-comment-input">
            {t('listing.comments.placeholder')}
          </label>
          <textarea
            id="listing-comment-input"
            className="listing-detail-comments__input"
            rows={3}
            maxLength={MAX_LEN}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t('listing.comments.placeholder')}
            disabled={posting}
          />
          <div className="listing-detail-comments__form-foot">
            <span className="listing-detail-comments__char-count">
              {draft.length}/{MAX_LEN}
            </span>
            <button type="submit" className="listing-detail-comments__submit" disabled={posting || !draft.trim()}>
              {posting ? t('listing.comments.submitting') : t('listing.comments.submit')}
            </button>
          </div>
        </form>
      ) : (
        <div className="listing-detail-comments__login">
          <p>{t('listing.comments.loginHint')}</p>
          <button type="button" className="listing-detail-comments__login-btn" onClick={() => onRequireLogin?.()}>
            {t('listing.comments.loginBtn')}
          </button>
        </div>
      )}
    </section>
  )
}
